import { _ as _extends } from '../../_rollupPluginBabelHelpers-mO86zOh3.js';
import React, { forwardRef, useState, useRef } from 'react';
import useId from '@indeed/ihl-use-id';
import { useMergeRefs } from '../../hooks/useMergeRefs.js';
import { useOutsideInteraction } from '../../hooks/useOutsideInteraction.js';
import { ListboxProvider } from '../Listbox/useListboxContext.js';
import { useListbox } from '../Listbox/useListbox.js';
import { SelectListTrigger } from './SelectListTrigger.js';
import { SelectListFilter } from './SelectListFilter.js';
import escapeRegExp from '../../utils/escapeRegExp.js';
import { jsx } from '@emotion/react';
import { Popup } from '../Popup/Popup.js';
import { InteractiveList } from '../InteractiveList/InteractiveList.js';
import { Box } from '../Box/Box.js';
import { Portal } from '../Portal/Portal.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import '../Listbox/useSelectableCollectionKeyboard.js';
import '../Listbox/useTypeKeySearch.js';
import '../../hooks/useCollection.js';
import '../../hooks/useControllableState.js';
import '../../utils/warn.js';
import '@indeed/ifl-css';
import '../../utils/transition.js';
import '../../utils/createResponsive.js';
import '../../utils/styled.js';
import '@indeed/ifl-icons';
import '../Flex/Flex.js';
import '../Input/Input.js';
import '../../constants/index.js';
import '../../utils/mergeRefs.js';
import '../../hooks/useHasFocus.js';
import '../../hooks/useCheckValidity.js';
import '../Input/ChipInput.js';
import '../Input/styles.js';
import '../Input/AutosizeInput.js';
import '../../hooks/useIsomorphicLayoutEffect.js';
import '../Chip/ChipGroup.js';
import '../Chip/Chip.js';
import '@emotion/is-prop-valid';
import '../../hooks/useResponsiveValue.js';
import '../../hooks/useBreakpointIndex.js';
import '../Button/Button.js';
import '../../utils/transparentize.js';
import '../Button/context.js';
import '../Spinner/Spinner.js';
import '../IconWrapper/IconWrapper.js';
import '../Label/Label.js';
import '../../utils/displayName.js';
import '../Input/InputWrapper.js';
import '../TouchTarget/TouchTarget.js';
import '../Text/Text.js';
import '@floating-ui/react-dom';
import 'react-dom';
import '../IndeedThemeProvider/IndeedThemeProvider.js';
import '../IndeedThemeProvider/utils.js';
import '../IndeedThemeProvider/theme.js';
import 'deepmerge';
import '@indeed/ifl-themes';
import '../LinkProvider/LinkProvider.js';

/**
 * @see https://experience.indeed.design/components/ids/select-list
 */
const SelectList = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    children,
    contentAfter,
    contentBefore,
    defaultSelectedValues,
    disabled = false,
    filterable = false,
    filterProps,
    invalid = false,
    onSelectionChange,
    onOpenChange,
    placeholder,
    popupProps,
    portaledPopup = false,
    selectedValues,
    selectionType = "single",
    setSelectionDisplayLabel,
    size = "md",
    ...remainingProps
  } = props;

  // ----------------------
  // STATE
  // ----------------------
  const [isOpen, setIsOpen] = useState(false);
  const [popupAnchor, setPopupAnchor] = useState(null);

  // ----------------------
  // IDs, REFS, VARIABLES
  // ----------------------
  const listboxId = `Listbox-${useId()}`;
  const popupId = `Popup-${useId()}`;
  const a11ySelectionId = `Selection-${useId()}`;
  const popupRef = useRef(null);
  const triggerRef = useRef(null);
  const filterRef = useRef(null);
  const mergedFilterRef = useMergeRefs(filterRef, filterProps?.ref ?? null);
  const mergedTriggerRef = useMergeRefs(forwardedRef, triggerRef, node => setPopupAnchor(node));
  const mergedPopupRef = useMergeRefs(popupRef, popupProps?.ref ?? null);
  const multiselect = selectionType === "multiple";

  // ----------------------
  // useListbox SETUP
  // ----------------------
  const handleOnSelectionChange = options => {
    if (!multiselect) {
      closePopup();
      triggerRef.current?.focus();
    }
    if (onSelectionChange) {
      onSelectionChange(options);
    }
  };
  const {
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    focusVisible,
    setFocusVisible,
    initializeVirtualFocus,
    onKeyDown: onListboxKeyDown,
    onKeyDownCapture: onListboxKeyDownCapture,
    collectionState
  } = useListbox({
    scrollContainerRef: popupRef,
    defaultSelectedValues,
    selectedValues,
    onSelectionChange: handleOnSelectionChange,
    multiselect
  });
  const getOptionsFromValues = React.useCallback(values => {
    return collectionState.getItemsByPredicate(option => values.includes(option.value));
  }, [collectionState]);

  // ----------------------
  // HANDLERS
  // ----------------------
  // Closes popup
  const closePopup = () => {
    setIsOpen(false);

    // Pass state to onOpenChange callback
    if (onOpenChange) {
      onOpenChange(false);
    }
    setActiveOption(undefined);
    setFocusVisible(false);
  };

  // Opens popup
  const openPopup = () => {
    setIsOpen(true);
    if (filterable) {
      // TODO: Figure out why timeout is necessary
      setTimeout(() => {
        filterRef.current?.focus();
      }, 10);
    }

    // Pass state to onOpenChange callback
    if (onOpenChange) {
      onOpenChange(true);
    }
  };

  // Open and close popup when trigger is clicked, using onMouseUp
  // to avoid spacebar/enter press to select options from closing popup
  const handleTriggerOnMouseUp = event => {
    if (isOpen) {
      closePopup();
    } else {
      openPopup();
    }
    remainingProps.onMouseUp?.(event);
  };

  // When filterable, only fire a user provided onBlur when focus leaves
  // the SelectList entirely, not when focus moves from the trigger to filter input
  const handleOnBlur = event => {
    // TODO: This was only working with a timeout for me, since checking document.activeElement
    // needs a tick to actually move focus to the filter input...
    setTimeout(() => {
      if (filterable && document.activeElement === filterRef.current) {
        return;
      }
      // If blur leaves SelectList, merge with user provided onBlur
      remainingProps.onBlur?.(event);
    }, 10);
  };

  // Closes popup on outside interaction, excluding the trigger
  const handleOutsideInteraction = () => {
    if (isOpen) {
      closePopup();
    }
  };
  useOutsideInteraction({
    refs: [popupRef, triggerRef],
    onOutsideInteraction: handleOutsideInteraction,
    disabled: !isOpen
  });

  //

  // ----------------------
  // INTERNAL PROPS
  // ----------------------
  const internalTriggerProps = {
    role: "combobox",
    "aria-expanded": isOpen,
    "aria-activedescendant": filterable || !isOpen ? undefined : activeOption?.id,
    "aria-haspopup": filterable ? "dialog" : "listbox",
    "aria-controls": filterable ? popupId : listboxId,
    onKeyDown(event) {
      const {
        key
      } = event;
      setFocusVisible(true);
      if (!isOpen) {
        if (key === "Enter" || key === " " || key === "ArrowDown" || key === "ArrowUp" || key.length === 1) {
          openPopup();
          initializeVirtualFocus();
        }
        onListboxKeyDownCapture(event);
      }
      if (isOpen && !filterable) {
        if (key === "Escape" || key === "Tab") {
          event.stopPropagation();
          closePopup();
        }
        onListboxKeyDown(event);
        onListboxKeyDownCapture(event);
      }
      remainingProps.onKeyDown?.(event);
    }
  };
  const internalFilterProps = {
    "aria-controls": listboxId,
    "aria-activedescendant": activeOption?.id,
    onKeyDown(event) {
      const {
        key
      } = event;
      if (key === "Escape" || key === "Tab") {
        closePopup();

        // TODO: Figure out why timeout is necessary
        setTimeout(() => {
          triggerRef.current?.focus();
        }, 10);
      }
      onListboxKeyDown(event);
      filterProps?.onKeyDown?.(event);
    },
    onChange(event) {
      const {
        value
      } = event.target;
      const matchingOption = collectionState.getItemsByPredicate(item => {
        const regex = new RegExp(escapeRegExp(value), "gi");
        return item && item.value && typeof item.value === "string" ? regex.test(item.value) : false;
      })[0];
      setFocusVisible(true);
      setActiveOption(matchingOption);
      filterProps?.onChange?.(event);
    }
  };

  // If popupProps or SelectList props includes an aria-label or aria-describedby,
  // pass it through to the Popup when `filterable`
  const getPopupAriaLabel = () => {
    if (popupProps && popupProps["aria-label"]) {
      return popupProps["aria-label"];
    } else if (props["aria-label"]) {
      return props["aria-label"];
    } else {
      return undefined;
    }
  };
  const getPopupAriaDescribedby = () => {
    if (popupProps && popupProps["aria-describedby"]) {
      return popupProps["aria-describedby"];
    } else if (props["aria-describedby"]) {
      return props["aria-describedby"];
    } else {
      return undefined;
    }
  };
  const internalPopupProps = {
    id: popupId,
    role: filterable ? "dialog" : undefined,
    "aria-label": filterable ? getPopupAriaLabel() : undefined,
    "aria-describedby": filterable ? getPopupAriaDescribedby() : undefined,
    onMouseDown(event) {
      /**
       * Prevent focus from moving to the body when clicking within the
       * popup and thus triggering a blur event.
       */
      if (event.target !== filterRef.current) {
        event.preventDefault();
      }
    }
  };
  const internalListboxProps = {
    role: multiselect ? "menu" : "listbox"
  };

  // ----------------------
  // UTILITIES
  // ----------------------
  // Create string to display in trigger
  const getSelectionDisplayLabel = () => {
    if (setSelectionDisplayLabel) {
      return setSelectionDisplayLabel(selectedOptionValues.map(value => value));
    } else if (multiselect) {
      return `${selectedOptionValues.length} selected`;
    } else {
      const selectedOptions = getOptionsFromValues(selectedOptionValues);
      return selectedOptions.length ? selectedOptions[0].text : "";
    }
  };

  // Create a11y label describing multiple selections
  const getSelectionA11yLabel = () => {
    if (selectedOptionValues.length > 0 && multiselect) {
      const selectedOptions = getOptionsFromValues(selectedOptionValues);
      return selectedOptions?.map(option => option.text).join(", ");
    } else {
      return undefined;
    }
  };

  // Merge additional aria-describedby ids with internally generated id
  const getAriaDescribedBy = () => {
    const ariaDescribedby = props["aria-describedby"];
    if (multiselect && ariaDescribedby) {
      return `${a11ySelectionId} ${ariaDescribedby}`;
    } else if (multiselect) {
      return `${a11ySelectionId}`;
    } else {
      return ariaDescribedby;
    }
  };

  // Create trigger placeholder
  const selectListPlaceholder = jsx(Box, {
    as: "span",
    sx: {
      color: "foreground.weak"
    }
  }, placeholder);

  // ----------------------
  // LISTBOX CONTEXT
  // ----------------------
  const context = {
    selectionType,
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    registerOption: collectionState.registerItem,
    focusVisible,
    setFocusVisible
  };

  // Popup to render inline or in a portal
  const SelectListPopup = jsx(Popup, _extends({}, popupProps, internalPopupProps, {
    ref: mergedPopupRef,
    alignment: "start",
    anchor: popupAnchor,
    sx: {
      overflowY: "auto",
      overflowX: "hidden",
      display: isOpen ? "block" : "none",
      paddingBlock: 3,
      paddingInline: 3,
      maxBlockSize: "262px",
      // Magic number to ensure last item is always partially shown to indicate scrollability
      inlineSize: popupAnchor?.offsetWidth,
      zIndex: 1,
      ...popupProps?.sx
    }
  }), filterable && jsx(SelectListFilter, _extends({}, filterProps, internalFilterProps, {
    ref: mergedFilterRef // Must be after filterProps spread so a ref passed there doesn't override this merged ref
  })), jsx(ListboxProvider, {
    value: context
  }, jsx(InteractiveList, _extends({
    id: listboxId
  }, internalListboxProps), children)));
  return jsx(Box, {
    sx: {
      boxSizing: "border-box",
      position: "relative",
      inlineSize: "100%"
    },
    "aria-owns": portaledPopup && isOpen ? listboxId : undefined
  }, jsx(SelectListTrigger, _extends({}, remainingProps, internalTriggerProps, {
    ref: mergedTriggerRef // Must be after remainingProps spread so a ref passed there doesn't override this merged ref
    ,
    contentAfter: contentAfter,
    contentBefore: contentBefore,
    disabled: disabled,
    invalid: invalid,
    isOpen: isOpen,
    size: size,
    onMouseUp: handleTriggerOnMouseUp,
    onBlur: handleOnBlur,
    "aria-describedby": getAriaDescribedBy()
  }), selectedOptionValues.length === 0 ? selectListPlaceholder : getSelectionDisplayLabel()), portaledPopup ? jsx(Portal, null, SelectListPopup) : SelectListPopup, multiselect && jsx(VisuallyHidden, {
    id: a11ySelectionId
  }, getSelectionA11yLabel()));
});
SelectList.displayName = "SelectList";

export { SelectList };
